<template>
	<div
		class="g-block-image-collage"
		:class="{
			'g-block-image-collage--edit': editable,
			'is-active': editable && active,
			'g-block-image-collage--h5': h5,
		}"
		:style="gridStyle"
		@click="onWrapClick"
	>
		<div
			class="g-block-image-collage__main"
			:class="{ 'is-clickable': isItemClickable(items[0]) }"
			:style="cellStyle"
			@click="onItemClick(items[0], $event)"
		>
			<img
				v-if="getImageUrl(items[0])"
				:src="getImageUrl(items[0])"
				alt=""
			/>
			<i v-else class="mbm-iconfont mbm-tupian"></i>
			<div v-if="items[0]?.title" class="g-block-image-collage__caption" :style="{ color: items[0].titleColor }">
				{{ items[0].title }}
			</div>
		</div>

		<div class="g-block-image-collage__side">
			<div
				class="g-block-image-collage__side-top"
				:class="{ 'is-clickable': isItemClickable(items[1]) }"
				:style="cellStyle"
				@click="onItemClick(items[1], $event)"
			>
				<img
					v-if="getImageUrl(items[1])"
					:src="getImageUrl(items[1])"
					alt=""
				/>
				<i v-else class="mbm-iconfont mbm-tupian"></i>
				<div v-if="items[1]?.title" class="g-block-image-collage__caption" :style="{ color: items[1].titleColor }">
					{{ items[1].title }}
				</div>
			</div>

			<div class="g-block-image-collage__side-bottom">
				<div
					v-for="index in [2, 3]"
					:key="items[index]?.id || index"
					class="g-block-image-collage__thumb"
					:class="{
						'is-clickable': isItemClickable(items[index]),
						'is-num': isNumCaption(items[index]),
					}"
					:style="cellStyle"
					@click="onItemClick(items[index], $event)"
				>
					<img
						v-if="getImageUrl(items[index])"
						:src="getImageUrl(items[index])"
						alt=""
					/>
					<i v-else class="mbm-iconfont mbm-tupian"></i>
					<div
						v-if="items[index]?.title"
						class="g-block-image-collage__caption"
						:style="{ color: items[index].titleColor }"
					>
						{{ items[index].title }}
					</div>
				</div>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import { navigateJumpLink } from '../../../utils/jumpLink.js';
	import {
		getImageCollageBlockStyle,
		getImageCollageImageUrl,
		normalizeImageCollageData,
	} from '../../../utils/imageCollage.js';

	export default {
		name: 'GImageCollageShell',
		components: { ImageGalleryLightbox },
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				lightboxVisible: false,
				lightboxUrls: [],
				lightboxIndex: 0,
			};
		},
		computed: {
			normalizedData() {
				return normalizeImageCollageData(this.data);
			},
			items() {
				return this.normalizedData.items;
			},
			blockStyle() {
				return getImageCollageBlockStyle(this.data, { h5: this.h5 });
			},
			gridStyle() {
				return {
					gap: this.blockStyle.gap,
					'--collage-gap': this.blockStyle.gap,
				};
			},
			cellStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			isPreviewable() {
				return this.h5 && !this.editable;
			},
		},
		methods: {
			getImageUrl(item) {
				return getImageCollageImageUrl(item?.image);
			},
			getGalleryUrls() {
				return this.items.map(item => this.getImageUrl(item)).filter(Boolean);
			},
			isNumCaption(item) {
				const title = String(item?.title || '').trim();
				return /^\d{1,2}$/.test(title);
			},
			isItemClickable(item) {
				if (!this.isPreviewable || !item) return false;
				return !!item.jumpLink?.type || !!this.getImageUrl(item);
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			onItemClick(item, e) {
				if (!this.isPreviewable || !item) return;
				e?.stopPropagation?.();
				if (item.jumpLink?.type) {
					navigateJumpLink(item.jumpLink, {
						apiRequest: this.$apiRequest,
					});
					return;
				}
				const urls = this.getGalleryUrls();
				if (!urls.length) return;
				const url = this.getImageUrl(item);
				const index = Math.max(0, urls.indexOf(url));
				this.lightboxUrls = urls;
				this.lightboxIndex = index;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-image-collage {
		display: flex;
		align-items: stretch;
		width: 100%;
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-image-collage__main {
		position: relative;
		flex: 1.45 1 0;
		min-width: 0;
		aspect-ratio: 10 / 9;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		&.is-clickable {
			cursor: pointer;
		}

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		i {
			font-size: 36px;
			color: #bfbfbf;
		}

		.g-block-image-collage--h5 & i {
			font-size: 0.72rem;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			height: 36%;
			background: linear-gradient(transparent, rgba(0, 0, 0, 0.4));
			pointer-events: none;
		}
	}

	.g-block-image-collage__side {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: var(--collage-gap, 8px);
	}

	.g-block-image-collage__side-top,
	.g-block-image-collage__thumb {
		position: relative;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 0;

		&.is-clickable {
			cursor: pointer;
		}

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		i {
			font-size: 24px;
			color: #bfbfbf;
		}

		.g-block-image-collage--h5 & i {
			font-size: 0.48rem;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			height: 45%;
			background: linear-gradient(transparent, rgba(0, 0, 0, 0.4));
			pointer-events: none;
		}
	}

	.g-block-image-collage__side-top {
		flex: 1 1 0;
	}

	.g-block-image-collage__side-bottom {
		flex: 1 1 0;
		display: flex;
		gap: var(--collage-gap, 8px);
		min-height: 0;
	}

	.g-block-image-collage__thumb {
		flex: 1 1 0;
		min-width: 0;

		&.is-num::after {
			display: none;
		}
	}

	.g-block-image-collage__caption {
		position: absolute;
		left: 10px;
		right: 10px;
		bottom: 10px;
		z-index: 1;
		font-size: 10px;
		font-weight: 500;
		line-height: 1.3;
		color: #fff;
		text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
		word-break: break-word;

		.g-block-image-collage--h5 & {
			left: 0.2rem;
			right: 0.2rem;
			bottom: 0.2rem;
			font-size: 0.20rem;
		}

		.g-block-image-collage__main & {
			font-size: 12px;

			.g-block-image-collage--h5 & {
				font-size: 0.3rem;
			}
		}
	}
</style>
